import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import type { Contract } from '../../lib/types';
import { SearchSelect } from '../../components/SearchSelect';
import { Pill, Spinner, Field, TextInput, TextArea, SelectInput, FormGrid, NumberInput } from '../../components/ui';
import { OperationEditor } from '../rework-batches/OperationEditor';

// 厂内过程返工（生产工单下的返修单；不交货；共用统一工序编辑器；原质检受控修订）。
// task-32/task-33 起：一张返修单可含多件工件（production_rework_items）。
//  · 新建：选生产合同 → GET /api/production-rework/contracts/:idOrNumber/workpieces 逐件勾选
//    → POST /api/production-rework/tasks {contract_id, trigger_point, problem, items[]} 创建一张多件单；
//    每件自动带出生产工单与该件「一件一检」的原质检（同件多 QC 时由用户选定一张）。
//  · 详情：头部可编辑问题/触发点、可删除返修单；工件列表逐件编辑/移除；
//    每件独立「返修工序」(/items/:itemId/operations) 与「原质检受控修订」(/items/:itemId/inspection-revisions)，
//    修订仍直接改原 QC（原单号不变、不新建 QC）。
//  · 兼容旧单（无 items）：按单件展示，沿用 /tasks/:id/operations 与 /tasks/:id/inspection-revisions。

type Tone = 'ok' | 'info' | 'warn' | 'bad' | 'neutral';

type Task = {
  id: string;
  number: string;
  work_order_id: string;
  work_order_number?: string | null;
  business_type?: string | null;
  trigger_point?: string | null;
  problem?: string | null;
  status?: string | null;
  source_inspection_id?: string | null;
  source_inspection_number?: string | null;
  contract_id?: string | null;
  contract_number?: string | null;
  asset_id?: string | null;
  contract_line_id?: string | null;
  customer_serial?: string | null;
  item_count?: number | null;
  created_at?: string;
};
type OpPowder = { id: string; powder_name?: string | null; item_name?: string | null; actual_quantity?: number | null; actual_unit?: string | null };
type Op = {
  id: string;
  operation_code?: string | null;
  operation_name?: string | null;
  planned_hours?: number | null;
  actual_hours?: number | null;
  operator_name?: string | null;
  executed_date?: string | null;
  status?: string | null;
  powder_usages?: OpPowder[];
};
// 原质检单回显（GET .../items/:itemId/source-inspection）
type SourceInspectionStage = {
  id: string;
  stage_key: string;
  stage_name?: string | null;
  stage_order?: number | null;
  result?: string | null;
  operator_name?: string | null;
  notes?: string | null;
  completed_at?: string | null;
  readings: Record<string, string | number | null>;
};
type SourceInspection = {
  inspection: {
    id: string; number?: string | null; inspection_date?: string | null;
    result?: string | null; revision?: number | null; release_state?: string | null;
    source_system?: string | null; serial_number?: string | null; inspector_name?: string | null;
  };
  stages: SourceInspectionStage[];
};
type Rev = { id: string; revision_no: number; reason?: string | null; result_before?: string | null; result_after?: string | null; stage_ref?: string | null; published_version?: number | null; changed_at?: string };
type InspectionRow = { id: string; number?: string; result?: string };

// 合同工件候选（GET /contracts/:idOrNumber/workpieces，task-32 契约）。
type WorkpieceInspection = {
  id: string;
  number?: string | null;
  inspection_date?: string | null;
  result?: string | null;
  serial_number?: string | null;
  asset_id?: string | null;
  revision?: number | null;
};
type Workpiece = {
  work_order_id: string;
  work_order_number?: string | null;
  work_order_status?: string | null;
  asset_id?: string | null;
  customer_serial?: string | null;
  internal_code?: string | null;
  item_code?: string | null;
  contract_line_id?: string | null;
  source_inspection_id?: string | null;
  inspection_number?: string | null;
  inspection_date?: string | null;
  result?: string | null;
  serial_number?: string | null;
  inspection_candidates?: WorkpieceInspection[];
  already_in_open_task?: boolean;
};
type WorkpiecesData = {
  contract?: { id?: string; number?: string; customer_id?: string; customer_code?: string; customer_name?: string; status?: string };
  pieces?: Workpiece[];
};
type ContractOption = Contract;

// 返修单详情里的工件（production_rework_items）。
type TaskItem = {
  id: string;
  task_id?: string;
  work_order_id: string;
  work_order_number?: string | null;
  asset_id?: string | null;
  contract_line_id?: string | null;
  source_inspection_id?: string | null;
  source_inspection_number?: string | null;
  inspection_number?: string | null;
  inspection_date?: string | null;
  inspection_result?: string | null;
  serial_number?: string | null;
  customer_serial?: string | null;
  item_code?: string | null;
  status?: string | null;
  sort_order?: number;
  operations?: Op[];
  inspection_revisions_count?: number;
};
type TaskDetailData = Task & {
  items?: TaskItem[];
  // 旧口径兼容字段（task-32 前）：整单工序 / 修订。
  operations?: Op[];
  inspection_revisions?: Rev[];
};

const TRIGGERS: { value: string; label: string }[] = [
  { value: 'in_operation', label: '工序中' },
  { value: 'after_operation', label: '工序后' },
  { value: 'final_inspection', label: '终检' },
  { value: 'self_check', label: '自检' },
];

const IDLE_WP_STATE = { loading: false, error: '', unavailable: false };

const isNotFound = (e: any) => e?.status === 404 || e?.status === 405;
const errText = (e: any, fallback: string): string => {
  const base = e?.message || fallback;
  const raw = e?.details;
  if (raw && typeof raw === 'object') {
    const holder = raw as Record<string, unknown>;
    const counts = (holder.counts && typeof holder.counts === 'object' ? holder.counts : holder) as Record<string, unknown>;
    const parts = Object.entries(counts)
      .filter(([, v]) => typeof v === 'number')
      .map(([k, v]) => `${k}=${v}`);
    if (parts.length) return `${base}（${parts.join('、')}）`;
  }
  return base;
};
const triggerLabel = (v?: string | null) => TRIGGERS.find((t) => t.value === v)?.label || v || '—';
const taskTone = (s?: string | null): Tone => (s === 'completed' ? 'ok' : s === 'cancelled' ? 'bad' : s === 'in_progress' ? 'warn' : 'info');

// 合同工件行的稳定 key 与展示取值。
const pieceKey = (p: Workpiece) => `${p.work_order_id}:${p.asset_id || p.serial_number || p.internal_code || ''}`;
const pieceSerial = (p: Workpiece) => p.serial_number || p.customer_serial || p.internal_code || '';
const pieceQcNumber = (p: Workpiece) => p.inspection_number || p.source_inspection_id || '';
// 一件一检：候选数组优先；旧/无候选时退化为当前 source_inspection_id 一条。
function pieceCandidates(p: Workpiece): WorkpieceInspection[] {
  if (Array.isArray(p.inspection_candidates) && p.inspection_candidates.length) return p.inspection_candidates;
  return p.source_inspection_id
    ? [{ id: p.source_inspection_id, number: p.inspection_number, inspection_date: p.inspection_date, result: p.result }]
    : [];
}

// 单件工件的「工序 + 原质检修订」工作区：按件端点（新）或整单端点（旧单兼容）。
// 注意：TaskDetail 必须以 key={item.id} 挂载本组件，切换工件时强制重建内部 state 与 OperationEditor，
// 否则 React 复用同一实例会把上一件的 ops/revs/表单状态带到下一件（e2e 曾出现第 2 件工序打到第 1 件 item）。
function ItemWorkspace({ itemId, endpoint, canWrite, serialLabel, inspectionLabel, hasInspection, onChanged }: {
  itemId: string;
  endpoint: string;
  canWrite: boolean;
  serialLabel: string;
  inspectionLabel: string;
  hasInspection: boolean;
  onChanged: () => void;
}) {
  const [ops, setOps] = useState<Op[]>([]);
  const [revs, setRevs] = useState<Rev[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [rev, setRev] = useState({ reason: '' });
  // 工序执行（2026-10-23 生产反馈：工程师不知道如何完成工序）：
  // 每行可填「实际工时 / 操作人」并 开始 → 完成；已完成的行显示完成标记。
  const [opEdit, setOpEdit] = useState<Record<string, { actual_hours: string; operator_name: string }>>({});
  // 原质检单（可调出修改读数）：展开后按阶段列出读数项，改动即登记受控修订。
  const [insp, setInsp] = useState<SourceInspection | null>(null);
  const [inspOpen, setInspOpen] = useState(false);
  const [inspEdit, setInspEdit] = useState<Record<string, Record<string, string>>>({});
  const [inspReason, setInspReason] = useState('');

  const load = useCallback(() => {
    setLoading(true); setErr('');
    Promise.all([
      api.list<Op>(`${endpoint}/operations`),
      api.list<Rev>(`${endpoint}/inspection-revisions`),
    ])
      .then(([o, r]) => { setOps(o); setRevs(r); })
      .catch((e: any) => setErr(e?.message || '加载失败'))
      .finally(() => setLoading(false));
  }, [endpoint]);
  useEffect(load, [load]);

  // 原质检单回显（仅当该件已关联原质检单）
  useEffect(() => {
    if (!hasInspection) { setInsp(null); return; }
    let alive = true;
    api.detail<SourceInspection | null>(`${endpoint}/source-inspection`)
      .then((d) => { if (alive) setInsp(d || null); })
      .catch(() => { if (alive) setInsp(null); });
    return () => { alive = false; };
  }, [endpoint, hasInspection]);

  const startOp = async (o: Op) => {
    setErr(''); setMsg('');
    setBusy(true);
    try {
      await api.patch(`${endpoint}/operations/${encodeURIComponent(o.id)}`, { status: 'in_progress' });
      setMsg(`工序「${o.operation_name || o.operation_code}」已开始`);
      load();
      onChanged();
    } catch (e: any) {
      setErr(errText(e, '开始工序失败'));
    } finally { setBusy(false); }
  };

  const finishOp = async (o: Op) => {
    const edit = opEdit[o.id] || { actual_hours: '', operator_name: '' };
    setErr(''); setMsg('');
    setBusy(true);
    try {
      const body: any = { status: 'completed' };
      if (edit.actual_hours !== '') body.actual_hours = Number(edit.actual_hours);
      if (edit.operator_name.trim()) body.operator_name = edit.operator_name.trim();
      await api.patch(`${endpoint}/operations/${encodeURIComponent(o.id)}`, body);
      setMsg(`工序「${o.operation_name || o.operation_code}」已完成`);
      setOpEdit((cur) => { const n = { ...cur }; delete n[o.id]; return n; });
      load();
      onChanged();
    } catch (e: any) {
      setErr(errText(e, '完成工序失败'));
    } finally { setBusy(false); }
  };

  const reopenOp = async (o: Op) => {
    setErr(''); setMsg('');
    setBusy(true);
    try {
      await api.patch(`${endpoint}/operations/${encodeURIComponent(o.id)}`, { status: 'in_progress' });
      setMsg('工序已回退为进行中');
      load();
      onChanged();
    } catch (e: any) {
      setErr(errText(e, '回退工序失败'));
    } finally { setBusy(false); }
  };

  // 登记备注（不判定结论：质检只记录数据，业务上不存在合格/不合格）
  const addRev = async () => {
    setErr(''); setMsg('');
    setBusy(true);
    try {
      await api.post(`${endpoint}/inspection-revisions`, { reason: rev.reason.trim() || '备注' });
      setMsg('备注已登记（原质检单号不变，未新建 QC）');
      setRev({ reason: '' });
      load();
      onChanged();
    } catch (e: any) {
      setErr(errText(e, '登记备注失败'));
    } finally {
      setBusy(false);
    }
  };

  // 保存某阶段的读数修改（受控修订：只改原单该阶段读数，原单号不变）
  // 业务口径（2026-10-23 用户明确）：「我们的质检是没有结论的，并不需要可接受或者不可接受，
  // 这个质检只是记录质检数据，给客户提供的也就是质检的报告，修改完质检数据保存即可。」
  // 因此：修订原因**不必填**（留空自动记为「读数修订」），也不提交任何结论字段。
  const saveStageReadings = async (stage: SourceInspectionStage) => {
    const edit = inspEdit[stage.stage_key];
    if (!edit || !Object.keys(edit).length) { setErr('请先修改至少一个读数项'); return; }
    setErr(''); setMsg('');
    setBusy(true);
    try {
      await api.post(`${endpoint}/inspection-revisions`, {
        reason: inspReason.trim() || '读数修订',
        stage_ref: stage.stage_key,
        readings_after_json: edit,
        // 明确不传 result_after：质检只记录数据，不做合格/不合格判定。
      });
      setMsg(`阶段「${stage.stage_name || stage.stage_key}」读数已保存（原质检单号不变）`);
      setInspEdit((cur) => { const n = { ...cur }; delete n[stage.stage_key]; return n; });
      setInspReason('');
      // 重新拉取原单最新读数
      const fresh = await api.detail<SourceInspection | null>(`${endpoint}/source-inspection`);
      setInsp(fresh || null);
      load();
      onChanged();
    } catch (e: any) {
      setErr(errText(e, '修订读数失败'));
    } finally { setBusy(false); }
  };

  if (loading) return <Spinner label="正在读取工序与修订…" />;

  return (
    <div data-testid="item-workspace" data-item-id={itemId} style={{ padding: 10, background: 'var(--panel)', borderTop: '1px solid var(--line-soft)' }}>
      {err ? <div className="notice bad">{err}</div> : null}
      {msg ? <div className="notice">{msg}</div> : null}

      <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
        当前工件：{serialLabel || itemId} · 原质检单 {inspectionLabel || '未关联'}
      </div>

      <div className="strong">返修工序</div>
      <div className="muted" style={{ fontSize: 12, margin: '2px 0 6px' }}>
        操作流程：安排工序后点「开始」→ 填实际工时/操作人 → 点「完成」。完成后再去做「原质检受控修订」。
      </div>
      <table className="grid" style={{ marginTop: 4 }}>
        <thead><tr><th>工序</th><th style={{ textAlign: 'right' }}>计划工时</th><th style={{ textAlign: 'right' }}>实际工时</th><th>状态</th><th>粉末耗用</th><th style={{ width: 300 }}>操作</th></tr></thead>
        <tbody>
          {ops.length === 0 ? <tr><td colSpan={6} className="muted">暂无工序</td></tr> : null}
          {ops.map((o) => {
            const st = String(o.status || 'pending');
            const edit = opEdit[o.id] || { actual_hours: '', operator_name: '' };
            return (
            <tr key={o.id} data-testid="op-row" data-op-id={o.id}>
              <td>{o.operation_name || o.operation_code || '—'}</td>
              <td style={{ textAlign: 'right' }}>{o.planned_hours ?? '—'}</td>
              <td style={{ textAlign: 'right' }}>{o.actual_hours ?? '—'}</td>
              <td className="muted">
                <Pill tone={st === 'completed' ? 'ok' : st === 'in_progress' ? 'warn' : 'neutral'} label={st} />
              </td>
              <td className="muted">
                {(o.powder_usages || []).length === 0 ? '—' : (o.powder_usages || []).map((u) => (
                  <div key={u.id}>{u.powder_name || u.item_name || '粉末'} {u.actual_quantity}{u.actual_unit || ''}</div>
                ))}
              </td>
              <td>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                  {st === 'pending' && canWrite ? (
                    <button className="btn" disabled={busy} onClick={() => void startOp(o)}>开始</button>
                  ) : null}
                  {st === 'in_progress' && canWrite ? (
                    <>
                      <div style={{ width: 76 }}>
                        <NumberInput
                          placeholder="实际工时"
                          aria-label={`实际工时 ${o.operation_name || o.operation_code}`}
                          value={edit.actual_hours}
                          onChange={(e) => setOpEdit((cur) => ({ ...cur, [o.id]: { ...edit, actual_hours: e.target.value } }))}
                        />
                      </div>
                      <div style={{ width: 96 }}>
                        <TextInput
                          placeholder="操作人"
                          aria-label={`操作人 ${o.operation_name || o.operation_code}`}
                          value={edit.operator_name}
                          onChange={(e) => setOpEdit((cur) => ({ ...cur, [o.id]: { ...edit, operator_name: e.target.value } }))}
                        />
                      </div>
                      <button className="btn" disabled={busy} onClick={() => void finishOp(o)}>完成</button>
                    </>
                  ) : null}
                  {st === 'completed' && canWrite ? (
                    <button className="btn" disabled={busy} onClick={() => void reopenOp(o)}>回退为进行中</button>
                  ) : null}
                  {st === 'completed' ? <span className="muted" style={{ fontSize: 12 }}>已完成</span> : null}
                </div>
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>

      {/* 与售后返修共用统一工序编辑器：机加工多选（各自工时）/ 激光熔覆（粉末+耗料+工时） */}
      <OperationEditor endpoint={endpoint} canWrite={canWrite} onChanged={() => { load(); onChanged(); }} />

      <div className="strong" style={{ marginTop: 10 }}>原质检修订历史</div>
      <div className="muted" style={{ fontSize: 12, margin: '2px 0 6px' }}>
        原质检单 {inspectionLabel || '未关联'}（直接修订原单、原单号不变，不新建 QC；质检只记录数据，不判定合格/不合格）
      </div>
      <table className="grid">
        <thead><tr><th>版本</th><th>修订原因</th><th>修订读数项</th><th>生效版本</th><th>时间</th></tr></thead>
        <tbody>
          {revs.length === 0 ? <tr><td colSpan={5} className="muted">暂无修订</td></tr> : null}
          {revs.map((r) => (
            <tr key={r.id}>
              <td>{r.revision_no}</td>
              <td className="muted">{r.reason || '—'}</td>
              <td className="muted">{r.stage_ref ? `阶段 ${r.stage_ref}` : '—'}</td>
              <td className="muted">{r.published_version ?? '—'}</td>
              <td className="muted">{r.changed_at || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {/* 业务口径（2026-10-23 用户明确）：质检只记录数据、不做合格/不合格判定，
          客户拿到的就是质检报告。因此这里不再提供「结论」选择，也不再强制填修订原因；
          改读数后直接「保存」即可。 */}
      {canWrite && hasInspection ? (
        <div className="detail-actions" style={{ marginTop: 6, flexWrap: 'wrap', gap: 6 }}>
          <div style={{ width: 260 }}>
            <TextInput placeholder="备注（选填，写入修订历史）" value={rev.reason} onChange={(e) => setRev((s) => ({ ...s, reason: e.target.value }))} />
          </div>
          <button className="btn" disabled={busy} onClick={() => void addRev()}>
            {busy ? '登记中…' : '登记备注'}
          </button>
          <span className="muted" style={{ fontSize: 12 }}>质检不判定合格/不合格，仅记录数据</span>
        </div>
      ) : canWrite ? (
        <div className="muted" style={{ fontSize: 12 }}>该件未关联原质检单，无法登记修订。</div>
      ) : null}

      {/* 调出原质检单修改读数（2026-10-23 生产反馈：工程师无法调出原质检单修改）。
          只改原单该阶段 readings，原单号不变、不新建 QC；每次保存都登记一条受控修订历史。 */}
      {canWrite && hasInspection ? (
        <div style={{ marginTop: 10 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <div className="strong">原质检单读数修订</div>
            <button className="btn" data-testid="toggle-source-inspection" onClick={() => setInspOpen((v) => !v)}>
              {inspOpen ? '收起原质检单' : '调出原质检单修改'}
            </button>
          </div>
          {inspOpen ? (
            <div style={{ marginTop: 6 }} data-testid="source-inspection-panel">
              {!insp ? (
                <div className="muted" style={{ fontSize: 12 }}>正在读取原质检单…（若长时间无内容，说明该件未关联原质检单）</div>
              ) : (
                <>
                  <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
                    {insp.inspection.number} · {insp.inspection.inspection_date || '—'}
                    {' · '}版本 {insp.inspection.revision ?? '—'} · 阶段 {insp.stages.length} 个（改完数值点「保存读数」即可）
                  </div>
                  <div style={{ width: 280, marginBottom: 6 }}>
                    <TextInput
                      placeholder="备注（选填，会写入修订历史）"
                      aria-label="读数修订原因"
                      value={inspReason}
                      onChange={(e) => setInspReason(e.target.value)}
                    />
                  </div>
                  {insp.stages.length === 0 ? (
                    <div className="muted" style={{ fontSize: 12 }}>该原质检单没有阶段明细，无法按阶段修改读数。</div>
                  ) : null}
                  {insp.stages.map((st) => {
                    const edit = inspEdit[st.stage_key] || {};
                    const keys = Object.keys(st.readings || {});
                    return (
                      <div key={st.id} style={{ border: '1px solid var(--line-soft)', borderRadius: 6, padding: 8, marginBottom: 6 }} data-testid="source-stage" data-stage-key={st.stage_key}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                          <div className="strong">{st.stage_name || st.stage_key}</div>
                          <span className="muted" style={{ fontSize: 12 }}>{st.completed_at || '未完成'}</span>
                        </div>
                        {keys.length === 0 ? (
                          <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>该阶段无读数项（如激光熔覆工序本身无测量项）。</div>
                        ) : (
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 6, marginTop: 6 }}>
                            {keys.map((k) => (
                              <label key={k} style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 12 }}>
                                <span className="muted">{k}</span>
                                <TextInput
                                  aria-label={`读数 ${st.stage_key} ${k}`}
                                  value={edit[k] !== undefined ? edit[k] : String(st.readings[k] ?? '')}
                                  onChange={(e) => setInspEdit((cur) => ({
                                    ...cur,
                                    [st.stage_key]: { ...(cur[st.stage_key] || {}), [k]: e.target.value },
                                  }))}
                                />
                              </label>
                            ))}
                          </div>
                        )}
                        {keys.length > 0 ? (
                          <div style={{ marginTop: 6, display: 'flex', gap: 8, alignItems: 'center' }}>
                            <button
                              className="btn"
                              data-testid="save-stage-readings"
                              disabled={busy || !Object.keys(edit).length}
                              onClick={() => void saveStageReadings(st)}
                            >
                              {busy ? '保存中…' : '保存读数'}
                            </button>
                            {!Object.keys(edit).length ? (
                              <span className="muted" style={{ fontSize: 12 }}>改动任一数值后即可保存</span>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// 返修单详情：头部编辑/删除 + 多工件列表 + 每件工序/修订。
function TaskDetail({ task, canWrite, onChanged, onDeleted }: {
  task: Task;
  canWrite: boolean;
  onChanged: () => void;
  /** 删除成功回调：父级负责关闭详情、本地移除该行并 reload 列表（可返回 Promise 供等待）。 */
  onDeleted: (taskId: string) => Promise<void> | void;
}) {
  const base = `/api/production-rework/tasks/${encodeURIComponent(task.id)}`;
  const [detail, setDetail] = useState<TaskDetailData | null>(null);
  const [wps, setWps] = useState<WorkpiecesData | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [headerEdit, setHeaderEdit] = useState<{ problem: string; trigger_point: string } | null>(null);
  const [expandedItem, setExpandedItem] = useState<string | null>(null);
  const [itemEdit, setItemEdit] = useState<Record<string, { serial_number: string; inspectionId: string; inspectionLabel: string }>>({});

  const load = useCallback((opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    setErr('');
    api.detail<TaskDetailData>(base)
      .then((d) => setDetail(d))
      .catch((e: any) => setErr(e?.message || '加载失败'))
      .finally(() => setLoading(false));
  }, [base]);
  useEffect(() => { load(); }, [load]);

  // 合同工件上下文：用于补全每件的工单号/序列号/原质检号（旧单无 contract_id 时跳过）。
  const contractId = detail?.contract_id || task.contract_id || '';
  useEffect(() => {
    if (!contractId) { setWps(null); return; }
    let alive = true;
    api.detail<WorkpiecesData>(`/api/production-rework/contracts/${encodeURIComponent(contractId)}/workpieces`)
      .then((d) => { if (alive) setWps(d || null); })
      .catch(() => { if (alive) setWps(null); });
    return () => { alive = false; };
  }, [contractId]);

  // 静默刷新：工序/修订变更后保持展开的工件与详情不闪回 Spinner。
  const reload = () => { load({ silent: true }); onChanged(); };

  const rawItems = detail?.items || [];
  const legacy = rawItems.length === 0;
  // 兼容旧单（无 items）：把整单当作单件展示，沿用旧整单端点。
  const items: TaskItem[] = legacy ? [{
    id: task.id,
    work_order_id: task.work_order_id,
    asset_id: task.asset_id || null,
    contract_line_id: task.contract_line_id || null,
    source_inspection_id: task.source_inspection_id || null,
    source_inspection_number: task.source_inspection_number || null,
    serial_number: task.customer_serial || null,
    status: task.status || null,
  }] : rawItems;

  const findPiece = (item: TaskItem): Workpiece | null => {
    const pieces = wps?.pieces || [];
    if (!pieces.length) return null;
    return pieces.find((p) => p.work_order_id === item.work_order_id && (
      (item.asset_id && p.asset_id === item.asset_id)
      || (item.serial_number && (
        p.serial_number === item.serial_number
        || p.customer_serial === item.serial_number
        || p.internal_code === item.serial_number
      ))
    )) || null;
  };
  const itemQcLabel = (item: TaskItem): string => {
    const wp = findPiece(item);
    // 旧单/后端未带质检号时：若该件原质检就是任务头部的原质检，用头部单号兜底，避免展示 UUID。
    const headerFallback = item.source_inspection_id && item.source_inspection_id === task.source_inspection_id
      ? task.source_inspection_number
      : null;
    return item.inspection_number || item.source_inspection_number || wp?.inspection_number || headerFallback
      || (item.source_inspection_id ? `QC ${String(item.source_inspection_id).slice(0, 8)}…` : '未关联');
  };
  const itemSerial = (item: TaskItem): string => {
    const wp = findPiece(item);
    return item.serial_number || wp?.serial_number || wp?.customer_serial || wp?.internal_code
      || (items.length === 1 ? task.customer_serial : null) || item.asset_id || '—';
  };
  const itemWorkOrder = (item: TaskItem): string => {
    const wp = findPiece(item);
    return item.work_order_number || wp?.work_order_number
      || (items.length === 1 ? task.work_order_number : null) || item.work_order_id;
  };
  const itemEndpoint = (item: TaskItem) =>
    (legacy ? base : `${base}/items/${encodeURIComponent(item.id)}`);

  const saveHeader = async () => {
    if (!headerEdit) return;
    if (!headerEdit.problem.trim()) { setErr('问题描述为必填'); return; }
    setBusy(true); setErr(''); setMsg('');
    try {
      await api.patch(base, { problem: headerEdit.problem.trim(), trigger_point: headerEdit.trigger_point });
      setMsg('返修单已更新');
      setHeaderEdit(null);
      reload();
    } catch (e: any) {
      setErr(errText(e, '更新失败'));
    } finally {
      setBusy(false);
    }
  };

  const removeTask = async () => {
    if (!window.confirm(`确认删除返修单 ${task.number}？将级联删除其工件与待执行工序，且不可恢复。`)) return;
    setBusy(true); setErr(''); setMsg('');
    try {
      await api.del(base);
      // 先清空本详情状态（防止旧单内容残留），再交给父级关闭详情 + 本地移除行 + await 列表刷新。
      setDetail(null);
      setMsg('返修单已删除');
      await Promise.resolve(onDeleted(task.id));
    } catch (e: any) {
      setErr(errText(e, '删除失败'));
    } finally {
      setBusy(false);
    }
  };

  const startItemEdit = (item: TaskItem) => {
    setItemEdit((cur) => ({
      ...cur,
      [item.id]: {
        serial_number: item.serial_number || '',
        inspectionId: item.source_inspection_id || '',
        inspectionLabel: item.inspection_number || item.source_inspection_number || item.source_inspection_id || '',
      },
    }));
  };

  const saveItem = async (item: TaskItem) => {
    const edit = itemEdit[item.id];
    if (!edit) return;
    const body: Record<string, unknown> = {};
    if ((edit.serial_number.trim() || '') !== (item.serial_number || '')) body.serial_number = edit.serial_number.trim() || null;
    if (edit.inspectionId !== (item.source_inspection_id || '')) body.source_inspection_id = edit.inspectionId || null;
    if (!Object.keys(body).length) {
      setItemEdit((cur) => { const next = { ...cur }; delete next[item.id]; return next; });
      return;
    }
    setBusy(true); setErr(''); setMsg('');
    try {
      await api.patch(`${base}/items/${encodeURIComponent(item.id)}`, body);
      setMsg('工件已更新');
      setItemEdit((cur) => { const next = { ...cur }; delete next[item.id]; return next; });
      reload();
    } catch (e: any) {
      setErr(errText(e, '更新工件失败'));
    } finally {
      setBusy(false);
    }
  };

  const removeItem = async (item: TaskItem) => {
    if (!window.confirm(`确认从本返修单移除工件「${itemSerial(item)}」？`)) return;
    setBusy(true); setErr(''); setMsg('');
    try {
      await api.del(`${base}/items/${encodeURIComponent(item.id)}`);
      setMsg('工件已移除');
      reload();
    } catch (e: any) {
      setErr(errText(e, '移除工件失败'));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Spinner label="正在读取返修单与工件…" />;

  const current = detail || task;
  const status = current.status || task.status;
  const problem = current.problem || task.problem || '';
  const triggerPoint = current.trigger_point || task.trigger_point || 'in_operation';
  const contractNumber = wps?.contract?.number || detail?.contract_number || task.contract_number || '';

  return (
    <div className="panel" style={{ margin: '6px 0', padding: 12, background: 'var(--surface-2, #f7f7f7)' }}>
      {err ? <div className="notice bad">{err}</div> : null}
      {msg ? <div className="notice">{msg}</div> : null}

      <div className="panel-head" style={{ padding: '0 0 8px', borderBottom: '1px solid var(--line-soft)', marginBottom: 8 }}>
        <div>
          <h2>返修单 {current.number || task.number}</h2>
          <div className="desc">
            合同 {contractNumber || '—'} · 触发点 {triggerLabel(triggerPoint)} · 状态 {status || 'open'}
            {legacy ? ' · 旧单（单件口径）' : ` · 工件 ${items.length} 件`}
          </div>
        </div>
        <div className="spacer" />
        {canWrite ? (
          <button
            className="btn"
            disabled={busy}
            onClick={() => setHeaderEdit(headerEdit ? null : { problem, trigger_point: triggerPoint })}
          >{headerEdit ? '取消编辑' : '编辑'}</button>
        ) : null}
        {canWrite ? (
          <button className="btn" disabled={busy} onClick={() => void removeTask()}>删除返修单</button>
        ) : null}
      </div>

      {headerEdit ? (
        <FormGrid>
          <Field label="触发点 *">
            <SelectInput value={headerEdit.trigger_point} onChange={(e) => setHeaderEdit((s) => (s ? { ...s, trigger_point: e.target.value } : s))}>
              {TRIGGERS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </SelectInput>
          </Field>
          <Field label="状态（只读）">
            <TextInput readOnly value={status || 'open'} />
          </Field>
          <Field label="问题描述 *" span required>
            <TextArea value={headerEdit.problem} onChange={(e) => setHeaderEdit((s) => (s ? { ...s, problem: e.target.value } : s))} />
          </Field>
          <div className="detail-actions" style={{ gridColumn: '1 / -1' }}>
            <button className="btn primary" disabled={busy} onClick={() => void saveHeader()}>{busy ? '保存中…' : '保存'}</button>
            <button className="btn" disabled={busy} onClick={() => setHeaderEdit(null)}>取消</button>
          </div>
        </FormGrid>
      ) : (
        <div className="muted" style={{ marginBottom: 8 }}>问题：{problem || '—'}</div>
      )}

      <div className="strong">返修工件（{items.length} 件）</div>
      <div className="muted" style={{ fontSize: 12, margin: '2px 0 6px' }}>
        一件一检：每件关联自己的原质检单；工序与质检修订按件分别记录（修订原单、不新建 QC）。
      </div>
      <table className="grid">
        <thead>
          <tr>
            <th>生产工单</th><th>工件 / 序列号</th><th>原质检</th><th>状态</th>
            <th style={{ width: 320 }}>操作</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const wp = findPiece(item);
            const editing = itemEdit[item.id];
            return (
              <Fragment key={item.id}>
                <tr data-item-id={item.id} data-testid="item-row">
                  <td className="muted">{itemWorkOrder(item)}</td>
                  <td className="muted">
                    {itemSerial(item)}
                    {item.item_code || wp?.item_code ? <span className="muted"> · {item.item_code || wp?.item_code}</span> : null}
                  </td>
                  <td className="muted">
                    {itemQcLabel(item)}
                    {item.inspection_date || wp?.inspection_date
                      ? ` · ${item.inspection_date || wp?.inspection_date}`
                      : ''}
                    {item.inspection_result || wp?.result
                      ? ` · ${item.inspection_result || wp?.result}`
                      : ''}
                    {item.inspection_revisions_count ? <span className="muted"> · 修订 {item.inspection_revisions_count}</span> : null}
                  </td>
                  <td><Pill tone={taskTone(item.status)} label={item.status || 'pending'} /></td>
                  <td>
                    <div className="detail-actions" style={{ flexWrap: 'wrap', gap: 6 }}>
                      <button className="btn" onClick={() => setExpandedItem(expandedItem === item.id ? null : item.id)}>
                        {expandedItem === item.id ? '收起工序/修订' : '工序/修订'}
                      </button>
                      {canWrite && !legacy ? (
                        <button className="btn" disabled={busy} onClick={() => startItemEdit(item)}>编辑工件</button>
                      ) : null}
                      {canWrite && !legacy && items.length > 1 ? (
                        <button className="btn" disabled={busy} onClick={() => void removeItem(item)}>移除</button>
                      ) : null}
                    </div>
                  </td>
                </tr>
                {editing ? (
                  <tr>
                    <td colSpan={5}>
                      <div className="detail-actions" style={{ flexWrap: 'wrap', gap: 8, alignItems: 'flex-end' }}>
                        <div style={{ width: 230 }}>
                          <div className="muted" style={{ fontSize: 11, marginBottom: 2 }}>工件序列号</div>
                          <TextInput
                            value={editing.serial_number}
                            placeholder="工件序列号 / 内部编码"
                            onChange={(e) => setItemEdit((cur) => (cur[item.id] ? { ...cur, [item.id]: { ...cur[item.id], serial_number: e.target.value } } : cur))}
                          />
                        </div>
                        <div style={{ width: 300 }}>
                          <div className="muted" style={{ fontSize: 11, marginBottom: 2 }}>原质检单（可留空）</div>
                          <SearchSelect<InspectionRow>
                            endpoint="/api/inspections"
                            value={editing.inspectionId || null}
                            label={editing.inspectionLabel}
                            optionTitle={(r) => r.number || r.id}
                            placeholder="检索原质检单…"
                            onChange={(r) => setItemEdit((cur) => (cur[item.id]
                              ? { ...cur, [item.id]: { ...cur[item.id], inspectionId: r?.id || '', inspectionLabel: r?.number || '' } }
                              : cur))}
                          />
                        </div>
                        <button className="btn primary" disabled={busy} onClick={() => void saveItem(item)}>保存</button>
                        <button
                          className="btn"
                          disabled={busy}
                          onClick={() => setItemEdit((cur) => { const next = { ...cur }; delete next[item.id]; return next; })}
                        >取消</button>
                      </div>
                    </td>
                  </tr>
                ) : null}
                {expandedItem === item.id ? (
                  <tr>
                    <td colSpan={5} style={{ padding: 0 }}>
                      <ItemWorkspace
                        key={item.id}
                        itemId={item.id}
                        endpoint={itemEndpoint(item)}
                        canWrite={canWrite}
                        serialLabel={itemSerial(item)}
                        inspectionLabel={itemQcLabel(item)}
                        hasInspection={Boolean(item.source_inspection_id)}
                        onChanged={reload}
                      />
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function ProductionReworkPage() {
  const [data, setData] = useState<{ rows: Task[]; total: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [openForm, setOpenForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  // 新建：生产合同 → 合同下工件候选。
  const [contract, setContract] = useState<{ id: string; label: string }>({ id: '', label: '' });
  const [wp, setWp] = useState<WorkpiecesData | null>(null);
  const [wpState, setWpState] = useState<{ loading: boolean; error: string; unavailable: boolean }>(IDLE_WP_STATE);
  const [picked, setPicked] = useState<Record<string, { piece: Workpiece; inspectionId: string }>>({});
  const [form, setForm] = useState({ trigger_point: 'in_operation', problem: '' });
  const wpReq = useRef(0);

  // 列表加载：返回 Promise，便于删除后 await 刷新（silent=true 时不清空表格，避免详情被卸载）。
  const load = useCallback((opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    setErr('');
    return api.detail<{ rows: Task[]; total: number }>('/api/production-rework/tasks')
      .then(setData)
      .catch((e: any) => setErr(e?.message || '加载失败'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { void load(); }, [load]);

  // 删除返修单成功：立即关闭详情、本地移除该行（不等网络）、清掉创建成功提示，随后 await 列表重载。
  // 否则旧 rows / 旧详情里的单号会在页面上残留（e2e 删除后仍能读到 PRW 单号）。
  const handleTaskDeleted = useCallback(async (taskId: string) => {
    setMsg('返修单已删除');
    setExpanded(null);
    setData((prev) => (prev
      ? { ...prev, rows: prev.rows.filter((r) => r.id !== taskId), total: Math.max(0, prev.total - 1) }
      : prev));
    await load({ silent: true });
  }, [load]);

  const loadWorkpieces = useCallback(async (contractId: string) => {
    const seq = wpReq.current + 1;
    wpReq.current = seq;
    setWpState({ loading: true, error: '', unavailable: false });
    try {
      const d = await api.detail<WorkpiecesData>(`/api/production-rework/contracts/${encodeURIComponent(contractId)}/workpieces`);
      if (seq !== wpReq.current) return;
      setWp(d || null);
      setWpState(IDLE_WP_STATE);
    } catch (e: any) {
      if (seq !== wpReq.current) return;
      setWp(null);
      // 仅当接口本身缺失（非 contract_not_found）时才提示后端未升级。
      const missingEndpoint = isNotFound(e) && e?.code !== 'contract_not_found';
      if (missingEndpoint) setWpState({ loading: false, error: '', unavailable: true });
      else setWpState({ loading: false, error: errText(e, '加载合同工件失败'), unavailable: false });
    }
  }, []);

  const changeContract = (c: ContractOption | null) => {
    const id = c?.id || '';
    // 不同合同的工件不能混入同一张返修单：切合同清空已选，并明确确认。
    if (id !== contract.id && Object.keys(picked).length > 0) {
      const ok = window.confirm('切换生产合同会清空已选工件（不同合同的工件不能混入同一张返修单），继续？');
      if (!ok) return;
    }
    setContract({ id, label: c ? `${c.number}${c.customer_name ? ` · ${c.customer_name}` : ''}` : '' });
    setPicked({});
    setWp(null);
    setWpState(IDLE_WP_STATE);
    if (id) void loadWorkpieces(id);
  };

  const pieces = wp?.pieces || [];
  const pickedList = Object.values(picked);
  const pickedWorkOrders = new Set(pickedList.map((x) => x.piece.work_order_id)).size;

  // 一件一检默认原质检：优先后端给的最新一张（source_inspection_id），否则候选第一张。
  const defaultInspectionId = (piece: Workpiece) => {
    const cands = pieceCandidates(piece);
    if (piece.source_inspection_id && (!cands.length || cands.some((c) => c.id === piece.source_inspection_id))) {
      return piece.source_inspection_id;
    }
    return cands[0]?.id || '';
  };
  const togglePiece = (piece: Workpiece, checked: boolean) => {
    const key = pieceKey(piece);
    setPicked((cur) => {
      const next = { ...cur };
      if (!checked) delete next[key];
      else next[key] = { piece, inspectionId: defaultInspectionId(piece) };
      return next;
    });
  };
  const setPieceInspection = (piece: Workpiece, inspectionId: string) => {
    const key = pieceKey(piece);
    setPicked((cur) => (cur[key] ? { ...cur, [key]: { ...cur[key], inspectionId } } : cur));
  };
  const selectAll = () => {
    setPicked(Object.fromEntries(pieces.map((p) => [pieceKey(p), { piece: p, inspectionId: defaultInspectionId(p) }])));
  };
  const clearPicked = () => setPicked({});

  const resetForm = () => {
    setOpenForm(false);
    setContract({ id: '', label: '' });
    setPicked({});
    setWp(null);
    setWpState(IDLE_WP_STATE);
    setForm({ trigger_point: 'in_operation', problem: '' });
  };

  const create = async () => {
    setErr(''); setMsg('');
    if (!contract.id) { setErr('请选择生产合同'); return; }
    const picks = Object.values(picked);
    if (!picks.length) { setErr('请至少选择一件工件'); return; }
    if (!form.problem.trim()) { setErr('问题描述为必填'); return; }
    const first = picks[0];
    setBusy(true);
    try {
      const out: any = await api.post('/api/production-rework/tasks', {
        contract_id: contract.id,
        trigger_point: form.trigger_point,
        problem: form.problem.trim(),
        // 兼容 task-32 之前的旧后端：header 至少带首件工件身份；新后端以 items 为准。
        work_order_id: first.piece.work_order_id,
        asset_id: first.piece.asset_id || undefined,
        contract_line_id: first.piece.contract_line_id || undefined,
        source_inspection_id: first.inspectionId || undefined,
        items: picks.map(({ piece, inspectionId }) => ({
          work_order_id: piece.work_order_id,
          asset_id: piece.asset_id || undefined,
          contract_line_id: piece.contract_line_id || undefined,
          source_inspection_id: inspectionId || undefined,
          serial_number: pieceSerial(piece) || undefined,
          item_code: piece.item_code || undefined,
        })),
      });
      const created = out?.data ?? out;
      const createdId = String(created?.id || '');
      setMsg(`厂内返工单已建立（${picks.length} 件工件${created?.number ? ` · ${created.number}` : ''}），已打开详情。`);
      resetForm();
      load();
      if (createdId) setExpanded(createdId);
    } catch (e: any) {
      setErr(errText(e, '创建失败'));
    } finally {
      setBusy(false);
    }
  };

  const contractLabelText = wp?.contract?.number || contract.label || '';

  return (
    <div>
      <div className="panel">
        <div className="panel-head">
          <div>
            <h2>厂内过程返工</h2>
            <div className="desc">
              按生产合同选择工件（可全部或部分）→ 自动关联生产工单与每件原质检（一件一检）→ 再按件添加工序；
              一张返修单可含多个工件；原质检按受控版本修订（原单号不变）；返修不含费用。
            </div>
          </div>
          <div className="spacer" />
          <button className="btn primary" onClick={() => (openForm ? resetForm() : setOpenForm(true))}>
            {openForm ? '收起' : '新建厂内返工'}
          </button>
        </div>
        {err ? <div className="notice bad">{err}</div> : null}
        {msg ? <div className="notice">{msg}</div> : null}

        {openForm ? (
          <>
            {wpState.loading ? <Spinner label="正在读取合同下可返修工件…" /> : null}
            {wpState.error ? <div className="notice bad">{wpState.error}</div> : null}
            {wpState.unavailable ? (
              <div className="notice">
                当前后端尚未提供「合同→工件」接口（GET /api/production-rework/contracts/:id/workpieces，task-32），
                暂不能按合同建单；请在后端升级后重试（旧单仍可在下方详情查看/删除）。
              </div>
            ) : null}
            <FormGrid>
              <Field label="生产合同 *" required>
                <SearchSelect<ContractOption>
                  endpoint="/api/contracts"
                  value={contract.id || null}
                  label={contract.label}
                  optionTitle={(c) => c.number}
                  optionMeta={(c) => c.customer_name}
                  placeholder="检索销售/生产合同…"
                  paginated
                  onChange={changeContract}
                />
              </Field>
              <Field label="触发点 *">
                <SelectInput value={form.trigger_point} onChange={(e) => setForm((s) => ({ ...s, trigger_point: e.target.value }))}>
                  {TRIGGERS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </SelectInput>
              </Field>
              <Field label="问题描述 *" span required>
                <TextArea value={form.problem} onChange={(e) => setForm((s) => ({ ...s, problem: e.target.value }))} placeholder="本返修单统一的返工问题…" />
              </Field>
            </FormGrid>

            {!contract.id ? (
              <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
                请先选择生产合同：系统会加载该合同下的可返修工件，并自动带出每件的生产工单与原质检单（一件一检）。
              </div>
            ) : null}

            {contract.id && !wpState.loading && !wpState.error && !wpState.unavailable ? (
              <div className="panel" style={{ marginTop: 8, padding: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span className="strong">合同 {contractLabelText} 可返修工件（{pieces.length} 件）</span>
                  <div className="spacer" />
                  <button className="btn" disabled={busy || pieces.length === 0} onClick={selectAll}>全选本页</button>
                  <button className="btn" disabled={busy || pickedList.length === 0} onClick={clearPicked}>清空</button>
                </div>
                <div className="muted" style={{ fontSize: 12, margin: '4px 0' }}>
                  可全部或部分勾选；每件自动带出其生产工单与该件原质检单（同件多张 QC 时请在行内下拉选定一张）。
                  已在返修中的工件会标记，提交时由后端校验。
                </div>
                {pickedList.length ? (
                  <div className="notice" style={{ margin: '4px 0' }}>
                    已选 <span className="strong">{pickedList.length}</span> 件（来自 <span className="strong">{pickedWorkOrders}</span> 张工单）。
                  </div>
                ) : null}
                {pieces.length === 0 ? (
                  <div className="notice">该合同下暂无可返修工件（工单可能未建立工件履历，或没有原质检记录）。</div>
                ) : (
                  <table className="grid" style={{ marginTop: 6 }}>
                    <thead>
                      <tr>
                        <th style={{ width: 40 }}></th><th>生产工单</th><th>工件 / 序列号</th>
                        <th>物料</th><th>原质检（一件一检）</th><th>状态</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pieces.map((p) => {
                        const key = pieceKey(p);
                        const on = Boolean(picked[key]);
                        const cands = pieceCandidates(p);
                        const currentInspection = picked[key]?.inspectionId ?? defaultInspectionId(p);
                        return (
                          <tr key={key}>
                            <td>
                              <input
                                type="checkbox"
                                checked={on}
                                onChange={(e) => togglePiece(p, e.target.checked)}
                                aria-label={`选择工件 ${pieceSerial(p) || p.asset_id || p.work_order_number || ''}`}
                              />
                            </td>
                            <td className="muted">{p.work_order_number || p.work_order_id}</td>
                            <td className="muted">{pieceSerial(p) || p.asset_id || '—'}</td>
                            <td className="muted">{p.item_code || '—'}</td>
                            <td>
                              {cands.length > 1 ? (
                                <select
                                  className="sel"
                                  value={currentInspection}
                                  disabled={!on}
                                  onChange={(e) => setPieceInspection(p, e.target.value)}
                                  aria-label={`选择原质检单（${pieceSerial(p) || '工件'}）`}
                                >
                                  <option value="">请选择原质检单…</option>
                                  {cands.map((c) => (
                                    <option key={c.id} value={c.id}>
                                      {c.number || c.id}{c.inspection_date ? ` · ${c.inspection_date}` : ''}{c.result ? ` · ${c.result}` : ''}
                                    </option>
                                  ))}
                                </select>
                              ) : p.source_inspection_id ? (
                                <span className="muted">
                                  {pieceQcNumber(p)}{p.inspection_date ? ` · ${p.inspection_date}` : ''}{p.result ? ` · ${p.result}` : ''}
                                </span>
                              ) : (
                                <span style={{ color: 'var(--bad)' }}>无原质检（提交后需人工补关联）</span>
                              )}
                            </td>
                            <td className="muted">
                              {p.already_in_open_task ? (
                                <span style={{ color: 'var(--bad)' }}>已在返修中</span>
                              ) : (p.work_order_status || '—')}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            ) : null}

            <div className="detail-actions" style={{ marginTop: 10 }}>
              <button
                className="btn primary"
                disabled={busy || !contract.id || pickedList.length === 0}
                onClick={() => void create()}
              >{busy ? '提交中…' : `创建返修单（${pickedList.length} 件）`}</button>
              <button className="btn" disabled={busy} onClick={resetForm}>取消</button>
            </div>
          </>
        ) : null}
      </div>

      <div className="panel">
        {loading ? <Spinner label="正在读取厂内返工任务…" /> : null}
        {!loading && data && data.rows.length === 0 ? <div className="notice">暂无厂内返工任务。</div> : null}
        {!loading && data && data.rows.length > 0 ? (
          <table className="grid">
            <thead>
              <tr>
                <th>任务号</th><th>生产工单</th><th>触发点</th><th>原质检</th><th>工件</th>
                <th>问题</th><th>状态</th><th></th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((t) => {
                const open = expanded === t.id;
                return (
                  <Fragment key={t.id}>
                    <tr>
                      <td>
                        <div className="strong">{t.number}</div>
                        {t.contract_number ? <div className="muted" style={{ fontSize: 11 }}>{t.contract_number}</div> : null}
                      </td>
                      <td className="muted">{t.work_order_number || '—'}</td>
                      <td className="muted">{triggerLabel(t.trigger_point)}</td>
                      <td className="muted">{t.source_inspection_number || '—'}</td>
                      <td className="muted">
                        {t.customer_serial || t.asset_id || (t.item_count ? `${t.item_count} 件工件` : '—')}
                      </td>
                      <td className="muted">{t.problem || '—'}</td>
                      <td><Pill tone={taskTone(t.status)} label={t.status || 'open'} /></td>
                      <td><button className="btn" onClick={() => setExpanded(open ? null : t.id)}>{open ? '收起' : '详情'}</button></td>
                    </tr>
                    {open ? (
                      <tr>
                        <td colSpan={8} style={{ padding: 0 }}>
                          <TaskDetail
                            task={t}
                            canWrite
                            onChanged={() => load({ silent: true })}
                            onDeleted={handleTaskDeleted}
                          />
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        ) : null}
      </div>

      {/* 旧口径入口：/rework 仍是历史 rework_records(in_house) 的只读查询页。
          新任务在本模块创建，历史记录只在这里留一个轻量跳转，不复制整页。 */}
      <div className="panel">
        <div className="panel-head">
          <div>
            <h2>历史厂内返工（旧口径）</h2>
            <div className="desc">
              本模块上线前的单件返工记录（rework_records, in_house）仍保留在旧页面；此处仅提供查询入口，不参与新任务流程。
            </div>
          </div>
          <div className="spacer" />
          <Link className="btn" to="/rework">查看历史厂内返工</Link>
        </div>
      </div>
    </div>
  );
}
